/**
 * InteractiveLineChart - A shared component for displaying interactive line charts with metric toggles.
 * 
 * Features:
 * - Click metric items to toggle visibility
 * - Hover metric items to highlight the corresponding line in the chart
 * - Select All/Deselect All button inline with metric toggles
 * - Consistent styling across all analytics pages
 * - Configurable title, metrics, and data
 */

import React, { ReactElement, useState, useCallback } from "react";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import { 
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, 
    CartesianGrid, Tooltip, Legend 
} from "recharts";
import { WidgetTheme, MetricConfig } from "../../types/analytics";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const LC: any = LineChart as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

const ChartContainer = styled.div<{ bgcolor: string }>`
    background-color: ${props => props.bgcolor};
    padding: 20px;
    border-radius: 5px;
    margin-bottom: 20px;
`;

export interface InteractiveLineChartProps {
    /** Chart title displayed at the top */
    title: string;
    /** Array of data points to display */
    data: Array<Record<string, any>>;
    /** The data key used for the X-axis (e.g., "datepartition", "date", "key") */
    xAxisDataKey: string;
    /** Metric configuration array defining available metrics */
    metrics: MetricConfig[];
    /** Object mapping metric keys to their initial visibility state */
    initialVisibleMetrics?: Record<string, boolean>;
    /** Theme configuration for colors */
    theme: WidgetTheme;
    /** Chart height in pixels (default: 400) */
    height?: number;
    /** Optional function to format X-axis tick labels */
    xAxisTickFormatter?: (value: string) => string;
    /** Optional function to format tooltip labels */
    tooltipLabelFormatter?: (value: string) => string;
    /** Optional function to format tooltip values */
    tooltipValueFormatter?: (value: number) => string;
    /** Optional Y-axis label */
    yAxisLabel?: string;
    /** Optional Y-axis domain [min, max] */
    yAxisDomain?: [number, number];
}

/**
 * Build default visible metrics state from metric config
 * By default, all metrics are visible
 */
function buildDefaultVisibleMetrics(metrics: MetricConfig[]): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    metrics.forEach(metric => {
        result[metric.key] = true;
    });
    return result;
}

/**
 * Interactive line chart component with metric visibility toggles and hover highlighting.
 */
export default function InteractiveLineChart({
    title,
    data,
    xAxisDataKey,
    metrics,
    initialVisibleMetrics,
    theme,
    height = 400,
    xAxisTickFormatter,
    tooltipLabelFormatter,
    tooltipValueFormatter,
    yAxisLabel,
    yAxisDomain
}: InteractiveLineChartProps): ReactElement {
    // Initialize visible metrics from props or default to all visible
    const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>(
        initialVisibleMetrics || buildDefaultVisibleMetrics(metrics)
    );
    const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);

    const handleMetricToggle = useCallback((key: string): void => {
        setVisibleMetrics(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    }, []);

    const handleToggleAll = useCallback((): void => {
        const allVisible = Object.values(visibleMetrics).every(v => v);
        const newState: Record<string, boolean> = {};
        metrics.forEach(metric => {
            newState[metric.key] = !allVisible;
        });
        setVisibleMetrics(newState);
    }, [visibleMetrics, metrics]);

    const handleMouseEnter = useCallback((key: string): void => {
        setHoveredMetric(key);
    }, []);

    const handleMouseLeave = useCallback((): void => {
        setHoveredMetric(null);
    }, []);

    const allVisible = Object.values(visibleMetrics).every(v => v);

    // Default X-axis formatter
    const defaultXAxisFormatter = (value: string): string => {
        try {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        } catch {
            return value;
        }
    };

    const xFormatter = xAxisTickFormatter || defaultXAxisFormatter;

    return (
        <ChartContainer bgcolor={theme.background}>
            <h5 style={{ color: theme.text, marginBottom: "15px" }}>{title}</h5>
            
            {/* Interactive Legend */}
            <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                marginBottom: "15px",
                flexWrap: "wrap",
                gap: "8px"
            }}>
                <div>
                    <span style={{ fontWeight: "bold", color: theme.text }}>
                        Visible Metrics
                    </span>
                    <div style={{ fontSize: "11px", color: theme.text, opacity: 0.6, marginTop: "2px" }}>
                        Click to toggle • Hover to highlight
                    </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                    {metrics.map(metric => {
                        const isHidden = !visibleMetrics[metric.key];
                        const isHovered = hoveredMetric === metric.key;
                        
                        return (
                            <div 
                                key={metric.key} 
                                onClick={() => handleMetricToggle(metric.key)}
                                onMouseEnter={() => handleMouseEnter(metric.key)}
                                onMouseLeave={handleMouseLeave}
                                style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: "4px",
                                    padding: "4px 8px",
                                    backgroundColor: isHovered ? "rgba(0,0,0,0.1)" : theme.background,
                                    borderRadius: "4px",
                                    border: `1px solid ${theme.border}`,
                                    cursor: "pointer",
                                    opacity: isHidden ? 0.4 : 1,
                                    textDecoration: isHidden ? "line-through" : "none"
                                }}
                            >
                                <span 
                                    style={{ 
                                        width: "12px", 
                                        height: "12px", 
                                        backgroundColor: metric.color,
                                        borderRadius: "2px",
                                        opacity: isHidden ? 0.4 : 1
                                    }} 
                                />
                                <span style={{ 
                                    fontSize: "12px", 
                                    color: theme.text,
                                    fontWeight: isHovered ? "bold" : "normal"
                                }}>
                                    {metric.name}
                                </span>
                            </div>
                        );
                    })}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleToggleAll}
                        style={{ 
                            padding: "4px 10px",
                            fontSize: "12px",
                            marginLeft: "4px"
                        }}
                    >
                        {allVisible ? "Deselect All" : "Select All"}
                    </Button>
                </div>
            </div>
            
            <RESP width="100%" height={height}>
                <LC data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CG strokeDasharray="3 3" />
                    <XA 
                        dataKey={xAxisDataKey} 
                        tickFormatter={xFormatter}
                    />
                    <YA 
                        domain={yAxisDomain}
                        label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft" } : undefined}
                    />
                    <TT 
                        contentStyle={{ 
                            backgroundColor: theme.background,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                        labelFormatter={tooltipLabelFormatter}
                        formatter={tooltipValueFormatter ? (value: number) => tooltipValueFormatter(value) : undefined}
                    />
                    <LG wrapperStyle={{ display: "none" }} />
                    
                    {metrics.map(metric => {
                        const isVisible = visibleMetrics[metric.key];
                        const isHovered = hoveredMetric === metric.key;
                        const hasHover = hoveredMetric !== null;
                        
                        if (!isVisible) return null;
                        
                        let opacity = 1;
                        if (hasHover && !isHovered) {
                            opacity = 0.3;
                        }
                        
                        return (
                            <LN 
                                key={metric.key}
                                type="monotone" 
                                dataKey={metric.key}
                                stroke={metric.color}
                                name={metric.name}
                                strokeWidth={isHovered ? 3 : 2}
                                dot={{ r: isHovered ? 4 : 3 }}
                                strokeOpacity={opacity}
                            />
                        );
                    })}
                </LC>
            </RESP>
        </ChartContainer>
    );
}
